import React, { useState, useCallback, useContext, useEffect, useReducer } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { Stack } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { green } from "@material-ui/core/colors";
import {
  Button,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Table,
  TableHead,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Box,
  TextField,
  InputAdornment,
  Chip,
} from "@material-ui/core";
import {
  Edit,
  CheckCircle,
  SignalCellularConnectedNoInternet2Bar,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  CropFree,
  DeleteOutline,
  Facebook,
  Instagram,
  WhatsApp,
  Search as SearchIcon,
  Sync as SyncIcon,
} from "@material-ui/icons";

import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import ChannelIcon from "../../components/ChannelIcon";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";
import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import WhatsAppModalCompany from "../../components/CompanyWhatsapps";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import WhatsappVersionModal from "../../components/WhatsappVersionModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import ForbiddenPage from "../../components/ForbiddenPage";

// Reducer para versões do WhatsApp
const versionsReducer = (state, action) => {
  switch (action.type) {
    case "LOAD_VERSIONS":
      const newVersions = action.payload;
      const existingIds = new Set(state.map(v => v.id));
      const uniqueNewVersions = newVersions.filter(v => !existingIds.has(v.id));
      return [...state, ...uniqueNewVersions];
    case "UPDATE_VERSION":
      const version = action.payload;
      const versionIndex = state.findIndex((v) => v.id === version.id);
      if (versionIndex !== -1) {
        // Atualizar na mesma posição
        const newState = [...state];
        newState[versionIndex] = version;
        return newState;
      } else {
        // Nova versão - adicionar no final
        return [...state, version];
      }
    case "DELETE_VERSION":
      const versionId = action.payload;
      return state.filter((v) => v.id !== versionId);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    borderRadius: "10px",
    boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  tooltip: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: theme.typography.pxToRem(14),
    border: "1px solid #dadde9",
    maxWidth: 450,
  },
  tooltipPopper: {
    textAlign: "center",
  },
  buttonProgress: {
    color: green[500],
  },
  TableHead: {
    backgroundColor: theme.palette.barraSuperior,
    color: "textSecondary",
    borderRadius: "5px",
  },
  tabsHeader: {
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(2),
  },
  syncButton: {
    marginLeft: theme.spacing(1),
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
      style={{ height: "calc(100% - 48px)", overflow: "auto" }}
    >
      {value === index && <Box p={2}>{children}</Box>}
    </div>
  );
}

const CustomToolTip = ({ title, content, children }) => {
  const classes = useStyles();

  return (
    <Tooltip
      arrow
      classes={{
        tooltip: classes.tooltip,
        popper: classes.tooltipPopper,
      }}
      title={
        <React.Fragment>
          <Typography gutterBottom color="inherit">
            {title}
          </Typography>
          {content && <Typography>{content}</Typography>}
        </React.Fragment>
      }
    >
      {children}
    </Tooltip>
  );
};

const IconChannel = (channel) => {
  return <ChannelIcon channel={channel} />;
};

const AllConnections = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const { list } = useCompanies();
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(true);
  const [loadingComp, setLoadingComp] = useState(false);
  const [whats, setWhats] = useState([]);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [filterConnections, setFilterConnections] = useState([]);
  const [companyWhatsApps, setCompanyWhatsApps] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Estados para versões do WhatsApp
  const [versions, dispatchVersions] = useReducer(versionsReducer, []);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [versionPageNumber, setVersionPageNumber] = useState(1);
  const [hasMoreVersions, setHasMoreVersions] = useState(false);
  const [versionSearchParam, setVersionSearchParam] = useState("");
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [deletingVersion, setDeletingVersion] = useState(null);
  const [versionConfirmModalOpen, setVersionConfirmModalOpen] = useState(false);

  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(
    confirmationModalInitialState
  );

  const history = useHistory();
  if (!user.super) {
    history.push("/tickets");
  }

  useEffect(() => {
    setLoadingWhatsapp(true);
    const fetchSession = async () => {
      try {
        const { data } = await api.get("/whatsapp/all/?session=0");
        setWhats(data);
        setLoadingWhatsapp(false);
      } catch (err) {
        setLoadingWhatsapp(false);
        toastError(err);
      }
    };
    fetchSession();
  }, []);

  // Socket listener para conexões (todas as empresas)
  useEffect(() => {
    if (!socket) return;

    // Listener genérico para qualquer empresa
    const handleWhatsappUpdate = (data) => {
      if (data.action === "update" && data.whatsapp) {
        setWhats((prevWhats) =>
          prevWhats.map((w) =>
            w.id === data.whatsapp.id ? { ...w, ...data.whatsapp } : w
          )
        );
        // Atualizar também o filterConnections se estiver aberto
        setFilterConnections((prevFilter) =>
          prevFilter.map((w) =>
            w.id === data.whatsapp.id ? { ...w, ...data.whatsapp } : w
          )
        );
      }
    };

    // Escutar eventos de todas as empresas carregadas
    const companyIds = [...new Set(whats.map((w) => w.companyId))];
    companyIds.forEach((companyId) => {
      socket.on(`company-${companyId}-whatsapp`, handleWhatsappUpdate);
    });

    return () => {
      companyIds.forEach((companyId) => {
        socket.off(`company-${companyId}-whatsapp`, handleWhatsappUpdate);
      });
    };
  }, [socket, whats.length]);

  // Carregar versões do WhatsApp
  useEffect(() => {
    if (tabValue !== 1) return;

    const delayDebounceFn = setTimeout(() => {
      const fetchVersions = async () => {
        try {
          setLoadingVersions(true);
          const { data } = await api.get("/whatsapp-versions", {
            params: { searchParam: versionSearchParam, pageNumber: versionPageNumber },
          });
          dispatchVersions({ type: "LOAD_VERSIONS", payload: data.records });
          setHasMoreVersions(data.hasMore);
          setLoadingVersions(false);
        } catch (err) {
          toastError(err);
          setLoadingVersions(false);
        }
      };

      fetchVersions();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [versionSearchParam, versionPageNumber, tabValue]);

  // Socket listener para versões
  useEffect(() => {
    const onWhatsappVersion = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatchVersions({ type: "UPDATE_VERSION", payload: data.record });
      }
      if (data.action === "delete") {
        dispatchVersions({ type: "DELETE_VERSION", payload: +data.id });
      }
      if (data.action === "sync") {
        setVersionPageNumber(1);
        dispatchVersions({ type: "RESET" });
      }
    };
    socket.on("whatsappVersion", onWhatsappVersion);

    return () => {
      socket.off("whatsappVersion", onWhatsappVersion);
    };
  }, [socket]);

  const responseFacebook = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then((response) => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setLoadingComp(true);
    try {
      const companyList = await list();
      setCompanies(companyList);
    } catch (e) {
      toast.error("Não foi possível carregar a lista de registros");
    }
    setLoadingComp(false);
  };

  const responseInstagram = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          addInstagram: true,
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then((response) => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = (whatsappsFilter, comp) => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
    if (whatsappsFilter?.length > 0) {
      setFilterConnections(whatsappsFilter);
      setCompanyWhatsApps(comp);
    }
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
    setFilterConnections([]);
    setCompanyWhatsApps(null);
  }, [setSelectedWhatsApp, setWhatsAppModalOpen]);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, [setQrModalOpen, setSelectedWhatsApp]);

  const handleEditWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId: whatsAppId,
      });
    }

    if (action === "delete") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId: whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }

    setConfirmModalInfo(confirmationModalInitialState);
  };

  // Handlers para versões
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenVersionModal = () => {
    setSelectedVersion(null);
    setVersionModalOpen(true);
  };

  const handleCloseVersionModal = () => {
    setSelectedVersion(null);
    setVersionModalOpen(false);
  };

  const handleVersionSearch = (event) => {
    const newSearchParam = event.target.value.toLowerCase();
    setVersionSearchParam(newSearchParam);
    setVersionPageNumber(1);
    dispatchVersions({ type: "RESET" });
  };

  const handleSetDefaultVersion = async (version) => {
    try {
      await api.put(`/whatsapp-versions/${version.id}`, { isDefault: true });
      toast.success(i18n.t("whatsappVersions.toasts.defaultSet"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteVersion = async (versionId) => {
    try {
      await api.delete(`/whatsapp-versions/${versionId}`);
      toast.success(i18n.t("whatsappVersions.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingVersion(null);
  };

  const handleSyncVersions = async () => {
    try {
      setSyncing(true);
      const { data } = await api.post("/whatsapp-versions/sync");
      toast.success(
        `${i18n.t("whatsappVersions.toasts.synced")}: ${data.synced} ${i18n.t("whatsappVersions.toasts.newVersions")}, ${data.skipped} ${i18n.t("whatsappVersions.toasts.updated")}`
      );
      setVersionPageNumber(1);
      dispatchVersions({ type: "RESET" });
    } catch (err) {
      toastError(err);
    } finally {
      setSyncing(false);
    }
  };

  const handleVersionScroll = (e) => {
    if (!hasMoreVersions || loadingVersions) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setVersionPageNumber((prev) => prev + 1);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const renderActionButtons = (whatsApp) => {
    return (
      <>
        {whatsApp.status === "qrcode" && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => handleOpenQrModal(whatsApp)}
          >
            {i18n.t("connections.buttons.qrcode")}
          </Button>
        )}
        {whatsApp.status === "DISCONNECTED" && (
          <>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleStartWhatsAppSession(whatsApp.id)}
            >
              {i18n.t("connections.buttons.tryAgain")}
            </Button>{" "}
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => handleRequestNewQrCode(whatsApp.id)}
            >
              {i18n.t("connections.buttons.newQr")}
            </Button>
          </>
        )}
        {(whatsApp.status === "CONNECTED" ||
          whatsApp.status === "PAIRING" ||
          whatsApp.status === "TIMEOUT") && (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => {
              handleOpenConfirmationModal("disconnect", whatsApp.id);
            }}
          >
            {i18n.t("connections.buttons.disconnect")}
          </Button>
        )}
        {whatsApp.status === "OPENING" && (
          <Button size="small" variant="outlined" disabled color="default">
            {i18n.t("connections.buttons.connecting")}
          </Button>
        )}
      </>
    );
  };

  const renderStatusToolTips = (whatsApp) => {
    return (
      <div className={classes.customTableCell}>
        {whatsApp.status === "DISCONNECTED" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.disconnected.title")}
            content={i18n.t("connections.toolTips.disconnected.content")}
          >
            <SignalCellularConnectedNoInternet0Bar color="secondary" />
          </CustomToolTip>
        )}
        {whatsApp.status === "OPENING" && (
          <CircularProgress size={24} className={classes.buttonProgress} />
        )}
        {whatsApp.status === "qrcode" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.qrcode.title")}
            content={i18n.t("connections.toolTips.qrcode.content")}
          >
            <CropFree />
          </CustomToolTip>
        )}
        {whatsApp.status === "CONNECTED" && (
          <CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
            <SignalCellular4Bar style={{ color: green[500] }} />
          </CustomToolTip>
        )}
        {(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.timeout.title")}
            content={i18n.t("connections.toolTips.timeout.content")}
          >
            <SignalCellularConnectedNoInternet2Bar color="secondary" />
          </CustomToolTip>
        )}
      </div>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      <ConfirmationModal
        title={i18n.t("whatsappVersions.confirmationModal.deleteTitle")}
        open={versionConfirmModalOpen}
        onClose={() => setVersionConfirmModalOpen(false)}
        onConfirm={() => handleDeleteVersion(deletingVersion?.id)}
      >
        {i18n.t("whatsappVersions.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QrcodeModal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
      />
      <WhatsAppModalCompany
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        filteredWhatsapps={filterConnections}
        companyInfos={companyWhatsApps}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />
      <WhatsappVersionModal
        open={versionModalOpen}
        onClose={handleCloseVersionModal}
        versionId={selectedVersion?.id}
      />

      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <Paper
            className={classes.mainPaper}
            style={{ overflow: "hidden" }}
            variant="outlined"
          >
            <MainHeader>
              <Stack>
                <Typography
                  variant="h5"
                  color="black"
                  style={{
                    fontWeight: "bold",
                    marginLeft: "10px",
                    marginTop: "10px",
                  }}
                  gutterBottom
                >
                  {i18n.t("connections.title")}
                </Typography>
                <Typography
                  style={{ marginTop: "-10px", marginLeft: "10px" }}
                  variant="caption"
                  color="textSecondary"
                >
                  {i18n.t(
                    "connections.connectYourServiceChannelsToReceiveMessagesAndStartConversationsWithYourCustomers"
                  )}
                </Typography>
              </Stack>

              <MainHeaderButtonsWrapper>
                {tabValue === 1 && (
                  <>
                    <TextField
                      placeholder={i18n.t("whatsappVersions.searchPlaceholder")}
                      type="search"
                      value={versionSearchParam}
                      onChange={handleVersionSearch}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon style={{ color: "gray" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleOpenVersionModal}
                    >
                      {i18n.t("whatsappVersions.buttons.add")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleSyncVersions}
                      disabled={syncing}
                      className={classes.syncButton}
                      startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                    >
                      {i18n.t("whatsappVersions.buttons.sync")}
                    </Button>
                  </>
                )}
                {tabValue === 0 && (
                  <PopupState variant="popover" popupId="demo-popup-menu">
                    {(popupState) => (
                      <React.Fragment>
                        <Menu {...bindMenu(popupState)}>
                          <MenuItem
                            onClick={() => {
                              handleOpenWhatsAppModal();
                              popupState.close();
                            }}
                          >
                            <WhatsApp
                              fontSize="small"
                              style={{
                                marginRight: "10px",
                              }}
                            />
                            WhatsApp
                          </MenuItem>
                          <FacebookLogin
                            appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                            autoLoad={false}
                            fields="name,email,picture"
                            version="13.0"
                            scope="public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                            callback={responseFacebook}
                            render={(renderProps) => (
                              <MenuItem onClick={renderProps.onClick}>
                                <Facebook
                                  fontSize="small"
                                  style={{
                                    marginRight: "10px",
                                  }}
                                />
                                Facebook
                              </MenuItem>
                            )}
                          />

                          <FacebookLogin
                            appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                            autoLoad={false}
                            fields="name,email,picture"
                            version="13.0"
                            scope="public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                            callback={responseInstagram}
                            render={(renderProps) => (
                              <MenuItem onClick={renderProps.onClick}>
                                <Instagram
                                  fontSize="small"
                                  style={{
                                    marginRight: "10px",
                                  }}
                                />
                                Instagram
                              </MenuItem>
                            )}
                          />
                        </Menu>
                      </React.Fragment>
                    )}
                  </PopupState>
                )}
              </MainHeaderButtonsWrapper>
            </MainHeader>

            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              className={classes.tabsHeader}
            >
              <Tab label={i18n.t("connections.title")} />
              <Tab label={i18n.t("whatsappVersions.title")} />
            </Tabs>

            {/* Tab Conexões */}
            <TabPanel value={tabValue} index={0}>
              <Paper>
                <Table size="small">
                  <TableHead className={classes.TableHead}>
                    <TableRow style={{ color: "#fff" }}>
                      <TableCell style={{ color: "#fff" }} align="center">
                        {i18n.t("connections.client")}
                      </TableCell>
                      <TableCell style={{ color: "#fff" }} align="center">
                        {i18n.t("connections.connectedConnections")}
                      </TableCell>
                      <TableCell style={{ color: "#fff" }} align="center">
                        {i18n.t("connections.disconnectedConnections")}
                      </TableCell>
                      <TableCell style={{ color: "#fff" }} align="center">
                        {i18n.t("connections.totalConnections")}
                      </TableCell>
                      {user.profile === "admin" && (
                        <TableCell style={{ color: "#fff" }} align="center">
                          {i18n.t("connections.table.actions")}
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingWhatsapp ? (
                      <TableRowSkeleton />
                    ) : (
                      <>
                        {companies?.length > 0 &&
                          companies.map((company) => (
                            <TableRow key={company.id}>
                              <TableCell align="center">
                                {company?.name}
                              </TableCell>
                              <TableCell align="center">
                                {whats?.length &&
                                  whats.filter(
                                    (item) =>
                                      item?.companyId === company?.id &&
                                      item?.status === "CONNECTED"
                                  ).length}
                              </TableCell>
                              <TableCell align="center">
                                {whats?.length &&
                                  whats.filter(
                                    (item) =>
                                      item?.companyId === company?.id &&
                                      item?.status !== "CONNECTED"
                                  ).length}
                              </TableCell>
                              <TableCell align="center">
                                {whats?.length &&
                                  whats.filter(
                                    (item) => item?.companyId === company?.id
                                  ).length}
                              </TableCell>
                              {user.profile === "admin" && (
                                <TableCell align="center">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleOpenWhatsAppModal(
                                        whats.filter(
                                          (item) =>
                                            item?.companyId === company?.id
                                        ),
                                        company
                                      )
                                    }
                                  >
                                    <Edit />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        <TableRow className={classes.TableHead}>
                          <TableCell style={{ color: "#fff" }} align="center">
                            {i18n.t("connections.total")}
                          </TableCell>
                          <TableCell style={{ color: "#fff" }} align="center">
                            {whats?.length &&
                              whats.filter(
                                (item) => item?.status === "CONNECTED"
                              ).length}
                          </TableCell>
                          <TableCell style={{ color: "#fff" }} align="center">
                            {whats?.length &&
                              whats.filter(
                                (item) => item?.status !== "CONNECTED"
                              ).length}
                          </TableCell>
                          <TableCell style={{ color: "#fff" }} align="center">
                            {whats?.length && whats.length}
                          </TableCell>
                          {user.profile === "admin" && (
                            <TableCell
                              style={{ color: "#fff" }}
                              align="center"
                            ></TableCell>
                          )}
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </TabPanel>

            {/* Tab Versões do WhatsApp */}
            <TabPanel value={tabValue} index={1}>
              <Paper onScroll={handleVersionScroll} style={{ maxHeight: "calc(100vh - 300px)", overflow: "auto" }}>
                <Table size="small">
                  <TableHead className={classes.TableHead}>
                    <TableRow>
                      <TableCell style={{ color: "#fff" }} align="center">
                        {i18n.t("whatsappVersions.table.version")}
                      </TableCell>
                      <TableCell style={{ color: "#fff" }} align="center">
                        {i18n.t("whatsappVersions.table.default")}
                      </TableCell>
                      <TableCell style={{ color: "#fff" }} align="center">
                        {i18n.t("whatsappVersions.table.origin")}
                      </TableCell>
                      <TableCell style={{ color: "#fff" }} align="center">
                        {i18n.t("whatsappVersions.table.beta")}
                      </TableCell>
                      <TableCell style={{ color: "#fff" }} align="center">
                        {i18n.t("whatsappVersions.table.released")}
                      </TableCell>
                      <TableCell style={{ color: "#fff" }} align="center">
                        {i18n.t("whatsappVersions.table.expire")}
                      </TableCell>
                      <TableCell style={{ color: "#fff" }} align="center">
                        {i18n.t("whatsappVersions.table.actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {versions.map((version) => (
                      <TableRow key={version.id}>
                        <TableCell align="center">{version.version}</TableCell>
                        <TableCell align="center">
                          {version.isDefault ? (
                            <Chip
                              label={i18n.t("whatsappVersions.labels.default")}
                              color="primary"
                              size="small"
                            />
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => handleSetDefaultVersion(version)}
                              title={i18n.t("whatsappVersions.buttons.setDefault")}
                            >
                              <CheckCircle />
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={
                              version.isFromUrl
                                ? i18n.t("whatsappVersions.labels.fromUrl")
                                : i18n.t("whatsappVersions.labels.manual")
                            }
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {version.beta ? (
                            <Chip
                              label={i18n.t("whatsappVersions.labels.beta")}
                              color="secondary"
                              size="small"
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell align="center">{formatDate(version.released)}</TableCell>
                        <TableCell align="center">{formatDate(version.expire)}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeletingVersion(version);
                              setVersionConfirmModalOpen(true);
                            }}
                            disabled={version.isDefault}
                            title={
                              version.isDefault
                                ? i18n.t("whatsappVersions.labels.cannotDeleteDefault")
                                : i18n.t("whatsappVersions.buttons.delete")
                            }
                          >
                            <DeleteOutline />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {loadingVersions && <TableRowSkeleton columns={7} />}
                  </TableBody>
                </Table>
              </Paper>
            </TabPanel>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default AllConnections;
